import React, { useState } from 'react'
import { useBilling, TIER_CONFIG } from '../contexts/BillingContext'
import { X, ArrowUpRight, Star, Coins } from '@phosphor-icons/react'

const FEATURE_LABELS = {
  retention: 'Curva de retención',
  traffic_sources: 'Fuentes de tráfico',
  full_analytics: 'Analytics completas',
  all_templates: 'Plantilla narrativa',
  multi_channel: 'Múltiples canales',
  no_watermark: 'Exports sin marca de agua',
  credits: 'Créditos de generación',
  broll: 'Generación de B-Roll con IA',
}

const PLAN_HIGHLIGHTS = {
  pro: [
    '200 créditos/mes (rollover 60 días)',
    'Analytics completas (retención, tráfico)',
    'Hasta 3 canales de YouTube',
    'Plantillas narrativas avanzadas',
    'Sin marca de agua en exports',
  ],
}

export default function UpgradePrompt() {
  const {
    upgradePrompt,
    dismissUpgradePrompt,
    stripeConfig,
    openCheckout,
    purchaseCreditPack,
    creditPackages,
    isFreePlan,
  } = useBilling()
  const [showPacks, setShowPacks] = useState(false)

  if (!upgradePrompt) return null

  const { feature, required_plan } = upgradePrompt
  const featureLabel = FEATURE_LABELS[feature] ?? 'esta función'
  const planConfig = TIER_CONFIG[required_plan]
  const highlights = PLAN_HIGHLIGHTS[required_plan] ?? []
  const isCreditsPrompt = feature === 'credits'

  const handleUpgrade = () => {
    const priceId = stripeConfig?.prices?.pro_monthly
    if (priceId) {
      openCheckout(priceId)
    } else {
      window.location.href = '/pricing'
    }
    dismissUpgradePrompt()
  }

  const handleBuyPack = packId => {
    purchaseCreditPack(packId)
    dismissUpgradePrompt()
  }

  const availablePacks = creditPackages.filter(p => p.available)

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60">
      <div
        className="bg-ds-surface border border-ds-border w-full max-w-md"
        style={{ borderRadius: 2 }}
      >
        {/* Header */}
        <div className="flex items-start justify-between p-6 pb-4">
          <div className="flex items-center gap-3">
            <div
              className="w-9 h-9 bg-[rgba(212,43,30,0.08)] border border-[rgba(212,43,30,0.2)] flex items-center justify-center"
              style={{ borderRadius: 2 }}
            >
              <Star size={18} weight="fill" className="text-ds-red" />
            </div>
            <div>
              <p className="type-label text-ds-text">
                {isCreditsPrompt ? 'Sin créditos disponibles' : 'Actualiza a Pro'}
              </p>
              <p
                className="text-ds-muted text-xs mt-0.5"
                style={{ fontFamily: 'Barlow, sans-serif' }}
              >
                {isCreditsPrompt
                  ? 'Compra créditos o pásate a Pro con 200/mes'
                  : `${featureLabel} requiere el plan Pro`}
              </p>
            </div>
          </div>
          <button
            onClick={dismissUpgradePrompt}
            className="text-ds-muted hover:text-ds-text transition-colors mt-0.5"
          >
            <X size={16} />
          </button>
        </div>

        {/* Credit packs */}
        {(isCreditsPrompt || showPacks) && availablePacks.length > 0 && (
          <div className="px-6 pb-4">
            <p className="type-caption text-ds-muted mb-3 flex items-center gap-1.5">
              <Coins size={11} />
              Comprar créditos
            </p>
            <div className="space-y-2">
              {availablePacks.map(pack => (
                <button
                  key={pack.id}
                  onClick={() => handleBuyPack(pack.id)}
                  className="w-full flex items-center justify-between bg-ds-elevated hover:bg-[#222] border border-ds-border px-4 py-3 transition-all group"
                  style={{ borderRadius: 2 }}
                >
                  <div className="flex items-center gap-3">
                    <span
                      className="text-ds-text font-semibold text-sm"
                      style={{ fontFamily: 'Barlow, sans-serif' }}
                    >
                      {pack.credits} créditos
                    </span>
                    <span
                      className="text-ds-muted text-xs"
                      style={{ fontFamily: 'Barlow, sans-serif' }}
                    >
                      {pack.label}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span
                      className="text-ds-text font-bold text-sm"
                      style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
                    >
                      ${pack.price_usd}
                    </span>
                    <ArrowUpRight
                      size={13}
                      className="text-ds-muted group-hover:text-ds-text transition-colors"
                    />
                  </div>
                </button>
              ))}
            </div>
            {!isCreditsPrompt && (
              <p
                className="text-ds-muted text-xs mt-3 text-center"
                style={{ fontFamily: 'Barlow, sans-serif' }}
              >
                o pásate a Pro para 200 créditos/mes
              </p>
            )}
          </div>
        )}

        {/* Pro plan features */}
        {!showPacks && (
          <div className="px-6 pb-5">
            <p className="type-caption text-ds-muted mb-3">Incluido en Pro</p>
            <ul className="space-y-2">
              {highlights.map((item, i) => (
                <li
                  key={i}
                  className="flex items-center gap-2.5 text-sm text-ds-text"
                  style={{ fontFamily: 'Barlow, sans-serif' }}
                >
                  <span className="w-1 h-1 bg-ds-red shrink-0" style={{ borderRadius: 1 }} />
                  {item}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Price + CTA */}
        <div className="px-6 pb-6 space-y-3">
          {!showPacks && (
            <div className="flex items-baseline gap-1.5">
              <span
                className="text-3xl font-bold text-ds-text"
                style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
              >
                ${planConfig?.price_monthly}
              </span>
              <span className="text-ds-muted text-sm" style={{ fontFamily: 'Barlow, sans-serif' }}>
                /mes
              </span>
              <span
                className="text-ds-muted text-xs ml-1"
                style={{ fontFamily: 'Barlow, sans-serif' }}
              >
                · $19/mes facturado anualmente
              </span>
            </div>
          )}

          <button
            onClick={handleUpgrade}
            className="btn-accent w-full flex items-center justify-center gap-2 py-3"
          >
            Actualizar a Pro
            <ArrowUpRight size={15} weight="bold" />
          </button>

          {!isCreditsPrompt && availablePacks.length > 0 && (
            <button
              onClick={() => setShowPacks(v => !v)}
              className="w-full text-center text-ds-muted hover:text-ds-text text-xs py-1 transition-colors type-btn"
            >
              {showPacks ? 'Ver plan Pro' : 'Comprar créditos sin suscripción →'}
            </button>
          )}

          <button
            onClick={dismissUpgradePrompt}
            className="w-full text-center text-ds-muted hover:text-ds-text text-xs py-1 transition-colors"
            style={{ fontFamily: 'Barlow, sans-serif' }}
          >
            Continuar con plan gratuito
          </button>
        </div>
      </div>
    </div>
  )
}
